import React, { useState } from 'react';
import { KatAlert } from '@amzn/katal-react';

/**
 * Dismissible info banner shown once after the DUB (EU) -> ZAZ redirect, so
 * operators understand why the URL changed under them.
 *
 * Rendered only when {@link RegionRedirectBannerProps.show} is true — i.e. when
 * the page load consumed the `redirectedFrom=dub` marker appended by
 * `runRegionRedirect` (see `consumeRedirectMarker` in
 * `src/utils/RegionRedirect.ts`). The marker is stripped from the URL before
 * React mounts, so refreshes, bookmarks, and shared links never re-show it.
 *
 * The copy names the CURRENT host rather than a hardcoded one, because the
 * redirect runs on both front doors (Midway `prod.zaz.newton.aft.amazon.dev`
 * and AFT-X `newton.zaz.aftx.amazonoperations.app`) and each operator must be
 * told to bookmark the door they actually landed on.
 */
export interface RegionRedirectBannerProps
{
    /** Whether the operator landed on this page via the DUB -> ZAZ redirect. */
    show: boolean;
    /** Injectable for tests; defaults to `window.location`. */
    location?: Location;
}

const RegionRedirectBanner = (
    { show, location = window.location }: RegionRedirectBannerProps
): JSX.Element | null =>
{
    const [ dismissed, setDismissed ] = useState( false );

    if ( !show || dismissed ) return null;

    return (
        <KatAlert
            variant="info"
            header="You have been redirected"
            onDismiss={() => setDismissed( true )}
        >
            Newton for the EU region has moved from Dublin (DUB) to Zaragoza (ZAZ) —
            you are now on <strong>{location.host}</strong>. Your page and filters
            were preserved. Please update any bookmarks to use this URL.
        </KatAlert>
    );
};

export default RegionRedirectBanner;
